<!-- CancelImmediateConfirmModal.vue
     Modal genérico de confirmação para cancelamento imediato de assinatura.
     ─────────────────────────────────────────────────────────────
     Suporta três variantes (via prop `variant`) que ajustam apenas
     o texto explicativo, mantendo a estrutura visual idêntica:

       - "active-paid"   → sub ativa com pagamento aprovado.
                           Há crédito proporcional pelos dias não usados.
       - "paused"        → sub pausada (pgto aprovado em algum momento).
                           Há crédito proporcional pelos dias não usados.
       - "pending"       → sub autorizada no MP mas 1º pagamento ainda
                           não confirmado. NÃO há crédito (não houve cobrança).

     A action de fato (chamar o backend) fica no componente pai —
     este modal só comunica intenção e emite "confirm" / "close".
-->

<script setup>
import { computed, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  isCancelling: {
    type: Boolean,
    default: false,
  },
  // Variante semântica que controla o texto do modal.
  variant: {
    type: String,
    default: "active-paid",
    validator: (v) => ["active-paid", "paused", "pending"].includes(v),
  },
});

const emit = defineEmits(["close", "confirm"]);

// ── Conteúdo dinâmico por variante ──────────────────────────────────
// Mantém estrutura (título / corpo principal / nota destacada) idêntica;
// só muda o conteúdo. Facilita evolução: para adicionar uma 4ª variante,
// basta um novo case nos computeds abaixo.

const title = computed(() => "Cancelar assinatura?");

const bodyText = computed(() => {
  switch (props.variant) {
    case "paused":
      return "Sua assinatura está pausada e será encerrada imediatamente. Você será movido para o plano Free.";
    case "pending":
      return "Sua assinatura será cancelada. Como o pagamento ainda não foi confirmado, não há valor a estornar — você será movido para o plano Free.";
    case "active-paid":
    default:
      return "Sua assinatura será encerrada imediatamente. Você será movido para o plano Free.";
  }
});

// Nota destacada (caixinha amarela embaixo do corpo) — usada para
// comunicar o crédito proporcional. No caso "pending" não há nota,
// porque não há nada a creditar.
const note = computed(() => {
  switch (props.variant) {
    case "pending":
      return null; // sem nota — não há crédito a comunicar
    case "paused":
    case "active-paid":
    default:
      return "Se houver dias pagos não utilizados, o valor proporcional ficará disponível como crédito para futuras assinaturas.";
  }
});

function close() {
  if (props.isCancelling) return;
  emit("close");
}

function confirm() {
  if (props.isCancelling) return;
  emit("confirm");
}

function handleEsc(e) {
  if (e.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEsc);
});
</script>

<template>
  <transition name="modal-fade" appear>
    <div
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-immediate-title"
      @click.self="close"
    >
      <div class="modal-panel modal-panel--compact">
        <div class="modal-panel__icon">
          <i class="fas fa-bolt"></i>
        </div>

        <h4 id="cancel-immediate-title" class="modal-panel__title">
          {{ title }}
        </h4>

        <p class="modal-panel__body">{{ bodyText }}</p>

        <p v-if="note" class="modal-panel__body modal-panel__body--small">
          {{ note }}
        </p>

        <div class="modal-panel__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isCancelling"
            @click="close"
          >
            Voltar
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="isCancelling"
            @click="confirm"
          >
            <span v-if="isCancelling" class="btn-spinner"></span>
            <span>{{
              isCancelling ? "Cancelando..." : "Confirmar cancelamento"
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--danger-bg);
  border: 1px solid var(--danger-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--danger-text);
  font-size: 1.3rem;
  margin-bottom: 0.25rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
}

.modal-panel__body--small {
  font-size: 0.82rem;
  background: var(--warning-bg);
  border: 1px solid #facc15;
  color: var(--warning-text);
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  line-height: 1.45;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid var(--danger-bg);
  border-top-color: var(--danger-text);
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}
</style>